/**
 * ModernMediaLibraryTab - Enhanced media library with modern grid layout and carousel
 */
import React, { useState, useMemo } from 'react';
import { 
  Image as ImageIcon, Video, FileText, Search, Filter, Plus, Trash2, 
  Eye, Download, Move, Star, Settings, Grid, List, Sliders 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { useAdmin } from './AdminHooks';
import { cn } from '../../lib/utils';

const MediaTypeBadge = ({ type }) => {
  const typeConfig = {
    image: { icon: ImageIcon, className: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Image' },
    video: { icon: Video, className: 'bg-purple-500/20 text-purple-400 border-purple-500/30', label: 'Vidéo' },
    document: { icon: FileText, className: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Document' },
    instagram: { icon: ImageIcon, className: 'bg-pink-500/20 text-pink-400 border-pink-500/30', label: 'Instagram' }
  };

  const config = typeConfig[type] || { icon: FileText, className: 'bg-gray-500/20 text-gray-400', label: 'Fichier' };
  const Icon = config.icon;

  return (
    <Badge className={cn("flex items-center gap-1", config.className)}>
      <Icon size={12} />
      {config.label}
    </Badge>
  );
};

const MediaCard = ({ media, onToggleVisibility, onDelete, onEdit }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card 
      className="overflow-hidden transition-all duration-300 bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 hover:shadow-xl group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Media Preview */}
      <div className="overflow-hidden relative aspect-square bg-slate-900">
        {media.type === 'image' ? (
          <img 
            src={media.url} 
            alt={media.title || 'Media'} 
            className={cn(
              "w-full h-full object-cover transition-all duration-300",
              isHovered && "scale-110"
            )}
          />
        ) : media.type === 'video' ? (
          <div className="flex justify-center items-center w-full h-full bg-gradient-to-br from-purple-900 to-blue-900">
            <Video className="w-12 h-12 text-purple-400 opacity-50" />
          </div>
        ) : media.type === 'instagram' ? (
          <div className="flex justify-center items-center w-full h-full bg-gradient-to-br from-pink-900 to-red-900">
            <ImageIcon className="w-12 h-12 text-pink-400 opacity-50" />
          </div>
        ) : (
          <div className="flex justify-center items-center w-full h-full bg-gradient-to-br from-green-900 to-emerald-900">
            <FileText className="w-12 h-12 text-green-400 opacity-50" />
          </div>
        )}
        
        {/* Overlay */}
        <div className={cn(
          "absolute inset-0 bg-gradient-to-t to-transparent transition-opacity duration-300 from-black/80 via-black/20",
          isHovered ? "opacity-100" : "opacity-0"
        )}>
          <div className="absolute right-0 bottom-0 left-0 p-4">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="text-white backdrop-blur-sm bg-white/10 hover:bg-white/20"
              >
                <Eye size={14} />
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="text-white backdrop-blur-sm bg-white/10 hover:bg-white/20"
              >
                <Download size={14} />
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="text-white backdrop-blur-sm bg-white/10 hover:bg-white/20"
                onClick={() => onEdit(media)}
              >
                <Settings size={14} />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Type Badge */}
        <div className="absolute top-2 left-2">
          <MediaTypeBadge type={media.type} />
        </div>
        
        {/* Visibility Toggle */}
        <div className="absolute top-2 right-2">
          <Switch
            checked={media.visible}
            onCheckedChange={() => onToggleVisibility(media.id, !media.visible)}
            className="data-[state=checked]:bg-blue-600"
          />
        </div>
      </div>
      
      {/* Content */}
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-white line-clamp-1">
          {media.title || 'Sans titre'}
        </CardTitle>
        <CardDescription className="text-xs text-slate-400 line-clamp-2">
          {media.caption || 'Aucune description'}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pt-0">
        {media.tags && media.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {media.tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} className="text-xs bg-slate-700 text-slate-300">
                {tag}
              </Badge>
            ))}
            {media.tags.length > 3 && (
              <Badge className="text-xs bg-slate-700 text-slate-300">
                +{media.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
        
        <div className="flex justify-between items-center">
          <span className="text-xs text-slate-500">
            {new Date(media.createdAt).toLocaleDateString()}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(media.id)}
            className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const FeaturedMediaCarousel = ({ featuredItems, onReorder, onDelete, onUpdate }) => {
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader>
        <CardTitle className="flex gap-2 items-center text-white">
          <Star className="w-5 h-5 text-yellow-400" />
          Médias en Vedette
        </CardTitle>
        <CardDescription className="text-slate-400">
          Gérez les médias affichés sur la page d'accueil
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {featuredItems.map((item, index) => (
            <Card key={item.id} className="overflow-hidden bg-slate-900/50 border-slate-600">
              <div className="overflow-hidden aspect-video bg-slate-800">
                {item.media.type === 'image' ? (
                  <img 
                    src={item.media.url} 
                    alt={item.media.title} 
                    className="object-cover w-full h-full"
                  />
                ) : item.media.type === 'video' ? (
                  <div className="flex justify-center items-center w-full h-full bg-gradient-to-br from-purple-900 to-blue-900">
                    <Video className="w-8 h-8 text-purple-400 opacity-50" />
                  </div>
                ) : (
                  <div className="flex justify-center items-center w-full h-full bg-gradient-to-br from-green-900 to-emerald-900">
                    <FileText className="w-8 h-8 text-green-400 opacity-50" />
                  </div>
                )}
              </div>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <Badge className="text-blue-400 bg-blue-500/20 border-blue-500/30">
                    Position {index + 1}
                  </Badge>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onReorder(index, -1)}
                      disabled={index === 0}
                      className="text-slate-400 hover:text-white"
                    >
                      <Move className="w-4 h-4 rotate-180" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onReorder(index, 1)}
                      disabled={index === featuredItems.length - 1}
                      className="text-slate-400 hover:text-white"
                    >
                      <Move className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(item.id)}
                      className="text-slate-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs text-slate-400">Priorité</Label>
                    <Input
                      type="number"
                      value={item.priority}
                      onChange={(e) => onUpdate(item.id, { priority: parseInt(e.target.value || '0') })}
                      className="text-sm text-white bg-slate-800 border-slate-600"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Durée (secondes)</Label>
                    <Input
                      type="number"
                      value={item.durationSeconds}
                      onChange={(e) => onUpdate(item.id, { durationSeconds: parseInt(e.target.value || '0') })}
                      className="text-sm text-white bg-slate-800 border-slate-600"
                    />
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <Label className="text-xs text-slate-400">Visible</Label>
                  <Switch
                    checked={item.visible}
                    onCheckedChange={(checked) => onUpdate(item.id, { visible: checked })}
                    className="data-[state=checked]:bg-blue-600"
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const ModernMediaLibraryTab = () => {
  const {
    libraryList, libraryLoading, libraryFilter, libraryQuery, librarySelectedIds,
    setLibraryFilter, setLibraryQuery, setLibrarySelectedIds, fetchLibrary,
    updateLibraryMedia, deleteLibraryMedia, bulkDeleteLibrarySelected,
    featuredItems, featuredLoading, openSelectionModal, moveFeatured,
    deleteFeaturedItem, updateFeaturedItem, mediaSrc, siteMediaEnabled, setSiteMediaEnabled
  } = useAdmin();

  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [selectedMedia, setSelectedMedia] = useState(null);

  const filteredMedia = useMemo(() => {
    if (!libraryList) return [];
    
    let filtered = libraryList;
    
    if (libraryFilter !== 'all') {
      filtered = filtered.filter(media => media.type === libraryFilter);
    }
    
    if (libraryQuery) {
      filtered = filtered.filter(media => 
        (media.title && media.title.toLowerCase().includes(libraryQuery.toLowerCase())) ||
        (media.caption && media.caption.toLowerCase().includes(libraryQuery.toLowerCase())) ||
        (media.tags && media.tags.some(tag => tag.toLowerCase().includes(libraryQuery.toLowerCase())))
      );
    }
    
    return filtered;
  }, [libraryList, libraryFilter, libraryQuery]);

  const handleSelectMedia = (mediaId) => {
    setLibrarySelectedIds(prev => 
      prev.includes(mediaId) 
        ? prev.filter(id => id !== mediaId)
        : [...prev, mediaId]
    );
  };

  const handleBulkDelete = async () => {
    if (librarySelectedIds.length && window.confirm(`Supprimer ${librarySelectedIds.length} média(s) ?`)) {
      await bulkDeleteLibrarySelected();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Bibliothèque de Médias</h2>
          <p className="text-slate-400">
            {filteredMedia.length} média(s) dans la bibliothèque
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchLibrary}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
          >
            <Sliders size={16} className="mr-2" />
            Actualiser
          </Button>
          <Button
            onClick={() => openSelectionModal('event')}
            className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Plus size={16} className="mr-2" />
            Ajouter des Médias
          </Button>
        </div>
      </div>

      {/* Featured Media Section */}
      <FeaturedMediaCarousel
        featuredItems={featuredItems}
        featuredLoading={featuredLoading}
        onReorder={moveFeatured}
        onDelete={deleteFeaturedItem}
        onUpdate={updateFeaturedItem}
      />

      {/* Search and Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 w-4 h-4 transform -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Rechercher par titre, description ou tags..."
                value={libraryQuery}
                onChange={(e) => setLibraryQuery(e.target.value)}
                className="pl-10 text-white bg-slate-900 border-slate-600 placeholder-slate-400"
              />
            </div>
            
            <div className="flex gap-2">
              <div className="flex gap-2 items-center px-3 py-2 rounded-lg border bg-slate-900 border-slate-600">
                <Label className="text-slate-300">Afficher la Galerie</Label>
                <Switch
                  checked={siteMediaEnabled}
                  onCheckedChange={setSiteMediaEnabled}
                  className="data-[state=checked]:bg-blue-600"
                />
              </div>
              <Select value={libraryFilter} onValueChange={setLibraryFilter}>
                <SelectTrigger className="w-[140px] bg-slate-900 border-slate-600 text-white">
                  <Filter size={16} className="mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600">
                  <SelectItem value="all" className="text-white">Tous types</SelectItem>
                  <SelectItem value="image" className="text-white">Images</SelectItem>
                  <SelectItem value="video" className="text-white">Vidéos</SelectItem>
                  <SelectItem value="document" className="text-white">Documents</SelectItem>
                  <SelectItem value="instagram" className="text-white">Instagram</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="flex rounded-lg border bg-slate-900 border-slate-600">
                <Button
                  size="sm"
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    "rounded-r-none",
                    viewMode === 'grid' && "bg-blue-600 text-white"
                  )}
                >
                  <Grid size={16} />
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  onClick={() => setViewMode('list')}
                  className={cn(
                    "rounded-l-none",
                    viewMode === 'list' && "bg-blue-600 text-white"
                  )}
                >
                  <List size={16} />
                </Button>
              </div>
              
              {librarySelectedIds.length > 0 && (
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                  className="text-white bg-red-600 hover:bg-red-700"
                >
                  <Trash2 size={16} className="mr-2" />
                  {librarySelectedIds.length}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Media Grid */}
      {libraryLoading ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center">
            <div className="mx-auto mb-4 w-8 h-8 rounded-full border-b-2 border-blue-400 animate-spin"></div>
            <p className="text-slate-400">Chargement des médias...</p>
          </CardContent>
        </Card>
      ) : (
        <div className={cn(
          "gap-4",
          viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'space-y-4'
        )}>
          {filteredMedia.length === 0 ? (
            <Card className={cn(
              "col-span-full bg-slate-800/50 border-slate-700",
              viewMode === 'list' && "col-span-1"
            )}>
              <CardContent className="p-8 text-center">
                <ImageIcon size={48} className="mx-auto mb-4 text-slate-600" />
                <p className="text-slate-400">Aucun média trouvé</p>
              </CardContent>
            </Card>
          ) : (
            filteredMedia.map((media) => (
              <MediaCard
                key={media.id}
                media={media}
                onToggleVisibility={updateLibraryMedia}
                onDelete={deleteLibraryMedia}
                onEdit={() => setSelectedMedia(media)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

ModernMediaLibraryTab.propTypes = {};

export default ModernMediaLibraryTab;
